import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './AboutPage.css';
import meaganHeadshot from '../images/meagan-kuhl-headshot.webp';

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'auto',
    });
  }, []);

  return (
    <main className="about-page">
      <section className="about-hero">
        <div className="about-hero-copy">
          <p className="about-eyebrow">About Teamward</p>

          <h1>
            Better team experiences start with a better way to plan.
          </h1>

          <p>
            Teamward helps busy managers, HR teams, and executive assistants
            discover and plan better experiences for their teams.
          </p>

          <p>
            From outings and volunteer events to trainings, speakers, and
            company retreats, Teamward makes it easier to find options that
            fit your location, group size, budget, and goals.
          </p>
        </div>

        <aside className="about-hero-card">
          <span>Our Mission</span>
          <strong>Less searching. Better team experiences.</strong>
          <p>
            Give teams a simpler way to discover, compare, and plan
            experiences that bring people together.
          </p>
        </aside>
      </section>

      <section className="about-story-section">
        <div className="about-section-heading">
          <p className="about-eyebrow">Why Teamward Exists</p>
          <h2>Planning should not begin with hours of searching.</h2>
        </div>

        <div className="about-story-grid">
          <article className="about-info-card">
            <span className="about-card-number">01</span>
            <h3>A better starting point</h3>
            <p>
              Planning something for a team often sounds simple until you
              begin comparing venues, activities, group limits, pricing,
              availability, and logistics across dozens of websites.
            </p>
          </article>

          <article className="about-info-card">
            <span className="about-card-number">02</span>
            <h3>Options for every kind of team</h3>
            <ul>
              <li>Team outings and activities</li>
              <li>Trainings and workshops</li>
              <li>Speakers and keynotes</li>
              <li>Company retreats and off-sites</li>
              <li>Volunteer opportunities</li>
            </ul>
          </article>

          <article className="about-info-card">
            <span className="about-card-number">03</span>
            <h3>More than a directory</h3>
            <p>
              Teamward helps teams narrow their choices, build a package with
              a realistic budget, or get hands-on planning help when they need
              support comparing options and coordinating details.
            </p>
          </article>
        </div>
      </section>

      <section className="about-how-section">
        <div className="about-section-heading">
          <p className="about-eyebrow">How It Works</p>
          <h2>From an early idea to a plan that works.</h2>
        </div>

        <div className="about-steps">
          <article className="about-step">
            <span>1</span>
            <div>
              <h3>Find the right experience</h3>
              <p>
                Narrow the options by location, group size, budget, setting,
                and goals.
              </p>
            </div>
          </article>

          <article className="about-step">
            <span>2</span>
            <div>
              <h3>Build a package</h3>
              <p>
                For Denver events, combine a venue, experience, food, and
                support while your planning range updates as you go.
              </p>
            </div>
          </article>

          <article className="about-step">
            <span>3</span>
            <div>
              <h3>Get planning help</h3>
              <p>
                Use one planning request for personalized ideas, a custom
                package, vendor comparisons, or hands-on coordination.
              </p>
            </div>
          </article>
        </div>
      </section>

      <section className="about-founder-section">
        <div className="about-founder-visual">
          <img
            src={meaganHeadshot}
            alt="Meagan Kuhl, founder of Teamward"
          />
        </div>

        <div className="about-founder-copy">
          <p className="about-eyebrow">Meet the Founder</p>

          <h2>
            Built by someone who knows what event planning actually takes.
          </h2>

          <p>
            Teamward was founded by <strong>Meagan Kuhl</strong>, whose
            event-planning experience includes serving on the campus events
            committee at Colorado State University and helping lead
            large-scale events with hundreds of attendees.
          </p>

          <p>
            She has also planned events professionally, giving her firsthand
            experience with vendor coordination, timelines, budgets,
            logistics, and the many moving pieces required to bring an event
            together.
          </p>

          <p>
            Her previous work as an inside sales engineer for a commercial
            HVAC company also gave her business-to-business sales experience
            and strengthened her project-management and client-communication
            skills.
          </p>

          <p>
            She created Teamward to give managers, HR teams, and executive
            assistants a better starting point — one that makes it easier to
            discover strong options without spending hours searching
            scattered websites.
          </p>

          <p>
            In 2023, the concept that would become Teamward was selected as
            one of three winners of the Casper Start-Up Challenge, providing
            early validation for its approach to simplifying company event
            planning.
          </p>

          <div className="about-founder-highlights">
            <span>Large-scale event experience</span>
            <span>Professional event planning</span>
            <span>B2B sales + project coordination</span>
            <span>2023 Casper Start-Up Challenge Winner</span>
          </div>
        </div>
      </section>

      <section className="about-location-section">
        <div>
          <p className="about-eyebrow">Where We’re Starting</p>
          <h2>Local knowledge with room to grow.</h2>
        </div>

        <p>
          Teamward offers experience matching in <strong>Casper, Wyoming</strong>{' '}
          and <strong>Denver, Colorado</strong>. The self-serve package builder
          is currently available for Denver events.
        </p>
      </section>

      <section className="about-cta-section">
        <p className="about-eyebrow">Start Planning</p>
        <h2>Ready to plan something better for your team?</h2>
        <p>
          Find an experience, build a Denver package, or get help with the
          planning.
        </p>

        <div className="about-actions">
          <Link to="/browse" className="about-button about-button-primary">
            Find the Right Experience
          </Link>

          <Link to="/quote-builder" className="about-button about-button-secondary">
            Build a Package
          </Link>

          <Link to="/planning-assistance" className="about-button about-button-secondary">
            Get Planning Help
          </Link>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
